import React from "react";
import useDebounce from "../../lib/hooks/useDebounce";
import { Link } from "react-router-dom";
import gql from "graphql-tag";
import { Query, QueryResult } from "react-apollo";
import {
  Container,
  Heading,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Input,
  InputGroup,
  InputLeftAddon,
  CSSReset,
  Box,
} from "@chakra-ui/react";
import { Search2Icon } from "@chakra-ui/icons";
import { Waypoint } from "react-waypoint";
import { RouteComponentProps } from "react-router";
import { FaCheck, FaEdit, FaUserCog } from "react-icons/fa";
import moment from "moment-timezone";

const ADMIN_SEARCH_OBSERVATORY_BUNDLES = gql`
  query AdminSearchObservatoryBundles($offset: Int, $searchKeyword: String) {
    adminSearchObservatoryBundles(
      offset: $offset
      searchKeyword: $searchKeyword
    ) {
      id
      name
      isActive
      createdAt
      drilldown {
        id
        name
      }
    }
  }
`;

const SearchObservatoryBundles = (
  props: SearchObservatoryBundlesProps
): JSX.Element => {
  const {
    viewer: { roles },
  } = props;

  const {
    value: searchKeyword,
    deferredValue: deferredKeyword,
    setValue: setKeyword,
  } = useDebounce(250);

  const queryVariables = {
    searchKeyword: deferredKeyword,
    offset: 0,
  };

  const globalWindow = typeof window !== "undefined" ? window : null;

  const canCreateEditRolesRequired = ["ETR Research Admin", "ETR Master Admin"];

  const canAssignUserAccessRolesRequired = [
    "ETR Master Admin",
    "ETR Research Admin",
    "ETR Sales Admin",
  ];

  const canCreateEdit = roles.some((role: string) => {
    return canCreateEditRolesRequired.includes(role);
  });

  const canAssignUserAccess = roles.some((role: string) => {
    return canAssignUserAccessRolesRequired.includes(role);
  });

  return (
    <>
      <CSSReset />
      <Container maxW={1250}>
        <Flex mb={7}>
          <Heading as="h1" size="xl" mb={0}>
            Search Observatory Bundles
          </Heading>

          {canCreateEdit && (
            <Link to="/observatory-bundles/new" style={{ marginLeft: "auto" }}>
              <Button colorScheme={"green"} data-cy="create-bundle-button">
                Create New Observatory Bundle
              </Button>
            </Link>
          )}
        </Flex>
        <Flex gridGap={4} mb={7}>
          <InputGroup flex={3}>
            <InputLeftAddon>
              <Search2Icon />
            </InputLeftAddon>
            <Input
              data-cy="search"
              value={searchKeyword}
              placeholder="Keyword Search"
              onChange={(e) => setKeyword(e.target.value)}
            />
          </InputGroup>
        </Flex>
        <Query
          fetchPolicy="network-only"
          query={ADMIN_SEARCH_OBSERVATORY_BUNDLES}
          variables={queryVariables}
        >
          {({
            data,
            error,
            fetchMore,
          }: QueryResult<
            SearchObservatoryBundlesResults,
            SearchObservatoryBundlesVariables
          >) => {
            if (error) {
              return (
                <Text>Error while getting market array report bundles</Text>
              );
            }

            if (!data) {
              return <div>Unable to fetch market array report bundles.</div>;
            }

            return (
              <TableContainer>
                <Table variant="simple">
                  <Thead textAlign={"center"}>
                    <Tr>
                      <Th textAlign={"center"}>Active?</Th>
                      <Th>Name</Th>
                      <Th>Created At</Th>
                      {canCreateEdit && (
                        <Th data-cy="edit-bundle-header" textAlign={"center"}>
                          Edit
                        </Th>
                      )}
                      {canAssignUserAccess && (
                        <Th data-cy="edit-bundle-header" textAlign={"center"}>
                          Assign User Access
                        </Th>
                      )}
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data?.adminSearchObservatoryBundles?.length ? (
                      data.adminSearchObservatoryBundles.map((bundle, idx) => {
                        return (
                          <Tr key={bundle.id} data-cy="data-row">
                            <Td>
                              <Box display="flex" justifyContent="center">
                                {bundle.isActive ? <FaCheck /> : <div></div>}
                              </Box>
                            </Td>
                            <Td>{bundle.name}</Td>
                            <Td>
                              {moment
                                .tz(bundle.createAt, moment.tz.guess())
                                .format("MMM D YYYY, h:mma z")}
                            </Td>
                            {canCreateEdit && (
                              <Td data-cy="edit-bundle-icon">
                                <Box display="flex" justifyContent="center">
                                  <Link
                                    to={`observatory-bundles/${bundle.id}/edit`}
                                    style={{ color: "black" }}
                                  >
                                    <FaEdit />
                                  </Link>
                                </Box>
                              </Td>
                            )}
                            {canAssignUserAccess && (
                              <Td data-cy="assign-user-bundle-icon">
                                <Box display="flex" justifyContent="center">
                                  <Link
                                    to={`observatory-bundles/${bundle.id}/access`}
                                    style={{ color: "black" }}
                                  >
                                    <FaUserCog />
                                  </Link>
                                </Box>
                              </Td>
                            )}
                            {idx ===
                            data.adminSearchObservatoryBundles.length - 3 ? (
                              <Td>
                                <Waypoint
                                  scrollableAncestor={globalWindow}
                                  onEnter={() => {
                                    fetchMore({
                                      variables: {
                                        offset:
                                          data.adminSearchObservatoryBundles
                                            .length,
                                      },
                                      updateQuery: (
                                        previousResults,
                                        { fetchMoreResult }
                                      ) => {
                                        if (
                                          !fetchMoreResult
                                            ?.adminSearchObservatoryBundles
                                            .length
                                        ) {
                                          return previousResults;
                                        }
                                        return {
                                          adminSearchMarketArrayReportBundles: [
                                            ...previousResults.adminSearchObservatoryBundles,
                                            ...fetchMoreResult.adminSearchObservatoryBundles,
                                          ],
                                        };
                                      },
                                    });
                                  }}
                                />
                              </Td>
                            ) : (
                              <Td></Td>
                            )}
                          </Tr>
                        );
                      })
                    ) : (
                      <Tr>
                        <Td className="empty-table" colSpan={5}>
                          No results for observatory bundles.
                        </Td>
                        <Td></Td>
                        <Td></Td>
                        <Td></Td>
                        <Td></Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            );
          }}
        </Query>
      </Container>
    </>
  );
};

export default SearchObservatoryBundles;
interface ObservatoryBundle {
  id: string;
  name: string;
  isActive: boolean;
  createAt: string;
}

interface SearchObservatoryBundlesVariables {
  searchKeyword: string;
  offset: number;
}

interface SearchObservatoryBundlesResults {
  adminSearchObservatoryBundles: ObservatoryBundle[];
}

interface SearchObservatoryBundlesProps extends RouteComponentProps {
  viewer: {
    roles: string[];
  };
}
